// Lone Rider — Moto Lights buybox UI
//
// Binds the combined MTL4 + MTL9 buybox to the shared engine in
// lr-moto-lights-configurator.js. It holds NO catalogue logic of its own: what
// exists, what it costs and whether it can be bought are all the engine's answers.
// This file only paints them and collects the rider's clicks.
//
// THE JOURNEY
// Deliberately the same experience as the MTL9 PDP's five-step make configurator
// (lr-make-configurator.js): one step at a time, answering a step reveals and opens
// the next, the same custom .lr-select dropdowns, and Mount as opt-in ticks rather
// than a dropdown — Bar Clamp ships with the lights, so Under Nose and the BMW OEM
// Crash Bar mount are upgrades on top of it, and no tick means Bar Clamp.
//
// It reuses that configurator's CSS verbatim (.lr-config__step, .lr-select,
// .lr-config__checkbox in custom.css), so the two cannot drift apart visually.
//
// WHAT THIS ADDS OVER THAT ONE: keyboard support. The PDP's .lr-select is a div with
// a click handler, so it cannot be operated without a mouse. The same markup here is
// given the ARIA combobox roles and full key handling (Enter/Space, arrows, Home/End,
// Escape, type-ahead). Same look, reachable by keyboard.
//
// Reused from the theme, nothing reimplemented:
//   .lr-select / .lr-config__*              make-configurator's controls and CSS
//   .btn.btn--full.add-to-cart, .atc-price  product-form.liquid's button
//   .js-qty__wrapper                        theme.QtySelector, bound by theme.Product
//   [data-variant-slide-img]                the gallery's variant slide
//   moto-lights-notify + waitlist-form      the Klaviyo notify-me flow
//   ajaxProduct:added                       opens the theme cart drawer
(() => {
  'use strict';

  // Mount keys that are UPGRADES rather than the included mount. Kept in step with
  // the engine's MOUNT_ORDER, whose first entry is the included Bar Clamp.
  const INCLUDED_MOUNT = 'barclamp';

  const ARROW_SVG =
    '<svg viewBox="0 0 10 6" width="10" height="6" aria-hidden="true">' +
    '<path d="M0 0l5 6 5-6z" fill="currentColor"/></svg>';

  const STEPS = [
    { key: 'make', type: 'select' },
    { key: 'model', type: 'select' },
    { key: 'set', type: 'select' },
    // Every kit includes the MotoCAN V2 harness and there is nothing to choose, so
    // this is shown but locked — the rider can see what they are getting without
    // being asked a question that has one answer. Same step, in the same place, as
    // the MTL9 PDP's five-step configurator.
    { key: 'motocan', type: 'select', locked: true },
    { key: 'mount', type: 'checkbox' }
  ];

  // ---------- one .lr-select ----------
  //
  // Same DOM as buildStep() in lr-make-configurator.js, plus the ARIA that one is
  // missing. The list is a real listbox, the trigger a real combobox.
  function buildSelect(stepKey, labels, uid, locked) {
    const wrapper = document.createElement('div');
    wrapper.className = 'variant-wrapper variant-wrapper--dropdown js lr-config__step is-hidden';
    wrapper.setAttribute('data-lr-config-step', stepKey);

    const labelId = 'MLLabel-' + uid + '-' + stepKey;
    const listId = 'MLList-' + uid + '-' + stepKey;

    const label = document.createElement('label');
    label.className = 'variant__label';
    label.id = labelId;
    label.textContent = (labels.steps[stepKey] && labels.steps[stepKey].label) || stepKey;

    const inputWrap = document.createElement('div');
    inputWrap.className = 'variant-input-wrap';

    const cd = document.createElement('div');
    // .lr-select--locked is the theme's own locked-step styling (custom.css): no
    // arrow, no hover, list suppressed.
    cd.className = 'lr-select' + (locked ? ' lr-select--locked' : '');

    const trigger = document.createElement('div');
    trigger.className = 'lr-select__trigger';
    if (locked) {
      // Not a control: nothing to expand, nothing to choose. Left out of the tab
      // order and announced as a plain value so a screen reader does not offer a
      // listbox that cannot be opened.
      trigger.setAttribute('aria-labelledby', labelId);
    } else {
      trigger.setAttribute('role', 'combobox');
      trigger.setAttribute('tabindex', '0');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-controls', listId);
      trigger.setAttribute('aria-labelledby', labelId);
    }

    const labelEl = document.createElement('span');
    labelEl.className = 'lr-select__label';

    const arrow = document.createElement('span');
    arrow.className = 'lr-select__arrow';
    arrow.innerHTML = ARROW_SVG;

    trigger.appendChild(labelEl);
    if (!locked) trigger.appendChild(arrow);

    const list = document.createElement('ul');
    list.className = 'lr-select__list';
    list.id = listId;
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-labelledby', labelId);

    cd.appendChild(trigger);
    cd.appendChild(list);
    inputWrap.appendChild(cd);
    wrapper.appendChild(label);
    wrapper.appendChild(inputWrap);

    return { key: stepKey, wrapper, cd, trigger, labelEl, list, signature: null };
  }

  // ---------- the mount ticks ----------
  function buildCheckboxStep(stepKey, labels, uid) {
    const wrapper = document.createElement('div');
    wrapper.className = 'variant-wrapper js lr-config__step lr-config__step--checkbox is-hidden';
    wrapper.setAttribute('data-lr-config-step', stepKey);

    const label = document.createElement('label');
    label.className = 'variant__label';
    label.id = 'MLLabel-' + uid + '-' + stepKey;
    label.textContent = (labels.steps[stepKey] && labels.steps[stepKey].label) || stepKey;

    const group = document.createElement('div');
    group.className = 'lr-config__checkbox-group';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-labelledby', label.id);

    wrapper.appendChild(label);
    wrapper.appendChild(group);

    return { key: stepKey, wrapper, group, isCheckbox: true, rows: [], signature: null };
  }

  function bind(section) {
    if (section.__mlBound) return;
    section.__mlBound = true;

    const q = (sel) => section.querySelector(sel);
    const uid = (section.querySelector('[id^="ProductSection-"]') || {}).id || 'ml';

    const picker = q('[data-ml-picker]');
    const stepsHost = q('[data-ml-steps]');
    const setNote = q('[data-ml-set-note]');
    const atc = q('[data-ml-atc]');
    const atcPrice = q('[data-ml-atc-price]');
    const atcDivider = q('[data-ml-atc-divider]');
    const errorEl = q('[data-ml-error]');
    const notice = q('[data-ml-notice]');
    const buy = q('[data-ml-buy]');

    if (!picker || !stepsHost) return;

    // Translatable strings, rendered by the Liquid so Weglot can reach them.
    let labels;
    try {
      labels = JSON.parse(q('[data-ml-labels]').textContent);
    } catch (e) {
      console.error('[LR moto lights] labels JSON is invalid', e);
      return;
    }

    // Gallery: remember the starting image so a variant with no image of its own
    // restores it rather than leaving the previous bike's photo up.
    const slideImg = q('[data-variant-slide-img]');
    const thumbImg = q('[data-variant-thumb-img]');
    const originalSlideSrc = slideImg ? slideImg.getAttribute('src') : null;

    const hideError = () => { if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; } };
    const showError = (m) => { if (errorEl) { errorEl.textContent = m; errorEl.hidden = false; } };

    // ---------- build the steps once ----------
    const ui = {};
    STEPS.forEach((step) => {
      const built = step.type === 'checkbox'
        ? buildCheckboxStep(step.key, labels, uid)
        : buildSelect(step.key, labels, uid, step.locked);
      built.locked = !!step.locked;
      ui[step.key] = built;
      stepsHost.appendChild(built.wrapper);
    });

    const closeAllLists = () => {
      STEPS.forEach((s) => {
        const b = ui[s.key];
        if (!b.cd) return;
        b.cd.classList.remove('is-open');
        b.trigger.setAttribute('aria-expanded', 'false');
        b.trigger.removeAttribute('aria-activedescendant');
      });
    };

    const openList = (built) => {
      closeAllLists();
      built.cd.classList.add('is-open');
      built.trigger.setAttribute('aria-expanded', 'true');
    };

    window.MotoLightsConfigurator.ready().then((config) => {

      // "Other Brands" — the bare MTL9 / MTL4 sets for bikes with no MotoCAN fitment. Off
      // unless the section setting turns it on.
      if (section.hasAttribute('data-ml-other-brand')) config.enableOtherBrand();

      // Opens the next step that still needs an answer, so the rider is walked down
      // the journey instead of hunting for the next control. Mirrors
      // openNextUnanswered() in lr-make-configurator.js. Mount is skipped because it
      // is always pre-answered (ticked upgrade, or the included Bar Clamp).
      function openNextUnanswered(fromKey) {
        const state = config.state();
        const from = STEPS.findIndex((s) => s.key === fromKey);
        for (let i = from + 1; i < STEPS.length; i++) {
          const step = STEPS[i];
          if (step.type === 'checkbox') break;
          // Locked steps have no question to ask — step over them rather than
          // stopping on a dropdown that cannot open.
          if (step.locked) continue;
          if (state[step.key]) continue;
          const built = ui[step.key];
          if (built.wrapper.classList.contains('is-hidden')) break;
          // Never open an empty list.
          if (!built.list.querySelector('.lr-select__item[data-value]')) break;
          openList(built);
          return;
        }
        closeAllLists();
      }

      // --- wiring: dropdowns ---
      STEPS.filter((s) => s.type === 'select' && !s.locked).forEach((step) => {
        const built = ui[step.key];

        built.trigger.addEventListener('click', (e) => {
          // The document-level handler in lr-make-configurator.js closes every open
          // .lr-select on any click; stop it closing the one we are opening.
          e.stopPropagation();
          if (built.cd.classList.contains('is-open')) closeAllLists();
          else openList(built);
        });

        built.list.addEventListener('click', (e) => {
          const item = e.target.closest('.lr-select__item');
          if (!item || item.getAttribute('aria-disabled') === 'true') return;
          e.stopPropagation();
          const value = item.getAttribute('data-value');
          if (!value) return;
          closeAllLists();
          config.set(step.key, value);
          openNextUnanswered(step.key);
          built.trigger.focus();
        });

        // Keyboard. The theme's own .lr-select has none; this is the same control
        // made operable without a mouse, which the rest of the buybox already is.
        built.trigger.addEventListener('keydown', (e) => {
          const items = Array.from(
            built.list.querySelectorAll('.lr-select__item[data-value]:not([aria-disabled="true"])')
          );
          if (!items.length) return;

          const open = built.cd.classList.contains('is-open');
          const currentIdx = items.findIndex((el) => el.classList.contains('is-active'));

          const move = (to) => {
            items.forEach((el) => el.classList.remove('is-active'));
            const el = items[Math.max(0, Math.min(items.length - 1, to))];
            if (!el) return;
            el.classList.add('is-active');
            built.trigger.setAttribute('aria-activedescendant', el.id);
            // Keep the highlighted row in view in a long model list.
            if (typeof el.scrollIntoView === 'function') {
              el.scrollIntoView({ block: 'nearest' });
            }
          };

          switch (e.key) {
            case 'Enter':
            case ' ':
              e.preventDefault();
              if (!open) { openList(built); move(currentIdx < 0 ? 0 : currentIdx); return; }
              if (currentIdx > -1) {
                closeAllLists();
                config.set(step.key, items[currentIdx].getAttribute('data-value'));
                openNextUnanswered(step.key);
              }
              return;
            case 'ArrowDown':
              e.preventDefault();
              if (!open) openList(built);
              move(currentIdx < 0 ? 0 : currentIdx + 1);
              return;
            case 'ArrowUp':
              e.preventDefault();
              if (!open) openList(built);
              move(currentIdx < 0 ? items.length - 1 : currentIdx - 1);
              return;
            case 'Home':
              if (!open) return;
              e.preventDefault(); move(0); return;
            case 'End':
              if (!open) return;
              e.preventDefault(); move(items.length - 1); return;
            case 'Escape':
              if (!open) return;
              e.preventDefault(); closeAllLists(); return;
            default:
              // Type-ahead: jump to the next row starting with the typed letter.
              if (e.key.length !== 1 || e.metaKey || e.ctrlKey || e.altKey) return;
              if (!open) openList(built);
              var ch = e.key.toLowerCase();
              var start = currentIdx + 1;
              for (var i = 0; i < items.length; i++) {
                var idx = (start + i) % items.length;
                if (items[idx].textContent.trim().toLowerCase().indexOf(ch) === 0) { move(idx); break; }
              }
          }
        });
      });

      // Fills the green "What's in the box" list product-template renders, the same
      // way updateWitb() in lr-make-configurator.js does on the MTL4 / MTL9 pages:
      // every part of the variant's option text is an included item unless it reads
      // as an exclusion ("No ..."), and the Bar Clamp always ships with the lights.
      const witbIncl = q('#witb_incl_list');
      const witbExcl = q('#witb_excl_list');
      const parseOptionText = window.LRMakeConfigurator && window.LRMakeConfigurator.parseOptionText;

      // Box-list wording differs from the variant title, on every variant: any light
      // set is shortened to its MTL name, and the Bar Clamp is listed as the pair it
      // ships as. Matched loosely because the catalogue spells them several ways:
      //   "Motorcycle Lights 4 Set (2 Pods)", "MTL4 Motorcycle Lights set",
      //   "MTL4 Set"                                      -> "MTL4 Set (2 pods)"
      //   "Motorcycle Lights Set (2 Pods)", "MTL9 set"    -> "MTL9 Set (2 pods)"
      //                                     (the MTL9 bare set has no number)
      //   any Bar Clamp mount                             -> "Bar Clamp Mount Set (2 pcs)"
      //                                     (4 pcs with the MTL4 + MTL9 bundle — two sets)
      // Exclusions ("No MotoCAN V2") are left as written.
      function witbLabel(text, entry) {
        const t = String(text).trim();
        if (/\bno\b/i.test(t)) return t;
        if (/bar\s*clamp/i.test(t)) {
          return 'Bar Clamp Mount Set (' + (entry && entry.setKey === 'bundle' ? 4 : 2) + ' pcs)';
        }
        if (/\bset\b/i.test(t) && /mtl\s*[49]|motorcycle\s+lights/i.test(t)) {
          const n = t.match(/mtl\s*([49])|lights\s*([49])/i);
          return 'MTL' + ((n && (n[1] || n[2])) || '9') + ' Set (2 pods)';
        }
        return t;
      }

      function renderWitb(entry) {
        if (!witbIncl || !witbExcl) return;
        const parsed = entry && parseOptionText ? parseOptionText(entry.title) : null;
        if (!parsed) {
          witbIncl.innerHTML = '';
          witbExcl.innerHTML = '';
          return;
        }

        const included = [];
        const excluded = [];
        parsed.parts.forEach((part) => (/\bno\b/i.test(part) ? excluded : included).push(part));
        if (entry.mountKey !== INCLUDED_MOUNT) {
          included.push(config.catalogue.mountLabels.get(INCLUDED_MOUNT) || 'Bar Clamp Mount');
        }

        const li = (text) => {
          const el = document.createElement('li');
          el.className = 'product__items';
          el.textContent = witbLabel(text, entry);
          return el;
        };
        witbIncl.replaceChildren(...included.map(li));
        witbExcl.replaceChildren(...excluded.map(li));
      }

      // ---------- paint ----------
      config.subscribe((snap) => {
        const state = snap.state;

        // --- which steps are visible ---
        // A step appears once the one above it is answered. Make is always shown.
        ui.make.wrapper.classList.remove('is-hidden');
        // Other Brands has no bike to pick, so Make hands straight over to Lights.
        ui.model.wrapper.classList.toggle('is-hidden', !state.make || snap.otherBrand);
        ui.set.wrapper.classList.toggle('is-hidden', !state.model);
        // Mount appears with a light set, and only when there is a real choice —
        // a bike offering only the included Bar Clamp has nothing to tick.
        const upgrades = snap.options.mounts.filter((m) => m.value !== INCLUDED_MOUNT);
        ui.mount.wrapper.classList.toggle('is-hidden', !state.set || upgrades.length === 0);

        // --- Make ---
        renderList(ui.make, snap.options.makes.map((o) => ({
          value: o.value, label: o.other ? (labels.otherBrand || o.label) : o.label, disabled: false
        })), labels.steps.make.placeholder, state.make);

        // --- Model ---
        renderList(ui.model, snap.options.models.map((o) => ({
          value: o.value, label: o.label, disabled: false
        })), labels.steps.model.placeholder, state.model);

        // --- Lights ---
        // All three always listed. One the bike has no variant for stays VISIBLE but
        // disabled, so the bundle is discoverable rather than silently absent.
        // Other Brands is the exception: it only offers its bare sets, so the
        // sets it does not have are left out rather than listed as "not for this bike".
        let bundleMissing = false;
        const setRows = snap.options.sets.filter((s) => !snap.otherBrand || s.exists).map((s) => {
          if (s.value === 'bundle' && state.model && !s.exists) bundleMissing = true;
          return {
            value: s.value,
            label: labels.sets[s.value] || s.value,
            disabled: !!state.model && !s.exists,
            note: (!!state.model && !s.exists) ? labels.setUnavailable : ''
          };
        });
        renderList(ui.set, setRows, labels.steps.set.placeholder, state.set);

        if (setNote) {
          setNote.textContent = bundleMissing ? labels.bundleUnavailable : '';
          setNote.hidden = !bundleMissing;
        }

        // Other Brands + MTL4: the bare set has no MotoCAN V2, and MTL4 pods do not
        // work without it. Sits right under the Lights step so it cannot be missed.
        if (!ui.set.warning) {
          const warning = document.createElement('p');
          warning.className = 'lr-ml__warning';
          warning.setAttribute('role', 'alert');
          warning.hidden = true;
          ui.set.wrapper.appendChild(warning);
          ui.set.warning = warning;
        }
        const showMtl4Warning = snap.otherBrand && state.set === 'mtl4';
        ui.set.warning.textContent = showMtl4Warning ? (labels.mtl4OtherBrandWarning || '') : '';
        ui.set.warning.hidden = !showMtl4Warning;

        // --- Mount ticks ---
        renderMountTicks(ui.mount, snap, upgrades, config);

        // --- MotoCAN (locked, informational) ---
        // Appears with the light set: that is the point at which the rider has
        // picked a kit and the harness becomes part of what they are buying. It is
        // shown to say what is included, not to ask anything.
        ui.motocan.wrapper.classList.toggle('is-hidden', !snap.state.set);
        ui.motocan.labelEl.textContent = config.motocanLabel();
        // Why it cannot be changed. Built once, from the Liquid so Weglot sees it.
        if (!ui.motocan.note) {
          const note = document.createElement('p');
          note.className = 'lr-ml__note';
          ui.motocan.wrapper.appendChild(note);
          ui.motocan.note = note;
        }
        // Other Brands: no MotoCAN fitment, so the harness is shown as an unticked
        // tick that cannot be ticked — the rider sees it exists but is not in this kit.
        if (snap.otherBrand && !ui.motocan.offTick) {
          const tick = document.createElement('label');
          tick.className = 'lr-config__checkbox lr-ml__checkbox--disabled';
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.className = 'lr-config__checkbox-input';
          input.disabled = true;
          const text = document.createElement('span');
          text.className = 'lr-config__checkbox-text';
          text.textContent = config.catalogue.motocanLabel || 'MotoCAN V2';
          tick.appendChild(input);
          tick.appendChild(text);
          ui.motocan.wrapper.insertBefore(tick, ui.motocan.note);
          ui.motocan.offTick = tick;
        }
        ui.motocan.cd.parentNode.hidden = snap.otherBrand;
        if (ui.motocan.offTick) ui.motocan.offTick.hidden = !snap.otherBrand;
        // Other Brands has its own text per light set, highlighted in yellow — see
        // .lr-ml__note--highlight.
        ui.motocan.note.textContent = (snap.otherBrand
          ? (labels.motocanOtherBrand || {})[state.set]
          : labels.motocanIncluded) || '';
        ui.motocan.note.classList.toggle('lr-ml__note--highlight', snap.otherBrand);

        // --- price (the theme's own price block) ---
        const priceEl = q('[data-product-price]');
        if (priceEl) {
          const target = priceEl.querySelector('h3') || priceEl;
          target.textContent = snap.priceFormatted;
          const blockEl = priceEl.closest('.product-block--price');
          // No price for a bike that can only be signed up for (coming soon, sold out).
          if (blockEl) blockEl.classList.toggle('hide', !snap.price || snap.status === 'unavailable');
        }
        // Never a compare-at price here: the page is evergreen, no sale language.
        const compareWrap = q('[data-product-price-wrap]');
        if (compareWrap) compareWrap.classList.add('hide');

        // --- buy vs notify ---
        const buyable = snap.status === 'buyable';
        const unavailable = snap.status === 'unavailable';

        if (buy) buy.hidden = unavailable;
        if (notice) notice.hidden = !unavailable;

        if (atc) {
          atc.disabled = !buyable;
          if (atcPrice) atcPrice.textContent = buyable ? snap.priceFormatted : '';
          if (atcDivider) atcDivider.hidden = !buyable;
        }

        if (unavailable) {
          const soon = q('[data-ml-notice-soon]');
          const oos = q('[data-ml-notice-oos]');
          const isSoon = snap.reason === 'coming_soon' || snap.reason === 'no_variant';
          if (soon) soon.hidden = !isSoon;
          if (oos) oos.hidden = isSoon;
          // Hands the full selection (and the variant id when there is one) to the
          // shared Klaviyo handler.
          config.syncNotify(notice);
        }

        hideError();

        // --- what's in the box ---
        renderWitb(snap.entry);

        // --- gallery ---
        const src = snap.image || originalSlideSrc;
        if (slideImg && src && slideImg.getAttribute('src') !== src) {
          slideImg.setAttribute('src', src);
          if (slideImg.hasAttribute('data-photoswipe-src')) {
            slideImg.setAttribute('data-photoswipe-src', src);
          }
          if (thumbImg) thumbImg.setAttribute('src', src);

          // The slider instance is not on the element — the theme looks it up with
          // Flickity.data() (theme.js initProductSlider), and so do we.
          const product = section.querySelector('[data-section-type="product"]') || section;
          const slider = product.querySelector('[data-product-photos]');
          if (slider && window.Flickity && typeof Flickity.data === 'function') {
            const flkty = Flickity.data(slider);
            if (flkty && typeof flkty.goToSlide === 'function') {
              flkty.goToSlide(0);
              if (typeof flkty.resize === 'function') flkty.resize();
            }
          }
        }
      });

      // ---------- add to cart ----------
      if (atc) {
        atc.addEventListener('click', () => {
          const qtyEl = section.querySelector('.js-qty__num');
          const qty = Math.max(1, parseInt((qtyEl && qtyEl.value) || '1', 10) || 1);

          hideError();
          atc.classList.add('btn--loading');
          atc.disabled = true;

          config.addToCart(qty)
            .catch((err) => showError(
              (err && err.message) || 'Could not add to cart. Please try again.'
            ))
            .then(() => {
              atc.classList.remove('btn--loading');
              atc.disabled = config.resolve().status !== 'buyable';
            });
        });
      }
    }).catch((e) => {
      console.error('[LR moto lights] buybox could not start', e);
      showError('Sorry, the configurator could not load. Please refresh the page.');
    });

    // ---------- renderers ----------

    // Fills one .lr-select's list. Rebuilt only when the rows actually change, so a
    // repaint never tears out the row the rider is arrowing through.
    function renderList(built, rows, placeholder, value) {
      const signature = JSON.stringify(rows.map((r) => [r.value, r.label, r.disabled])) + '|' + value;
      if (built.signature === signature) return;
      built.signature = signature;

      built.list.innerHTML = '';

      let selectedLabel = '';
      rows.forEach((row, i) => {
        const li = document.createElement('li');
        li.className = 'lr-select__item' + (row.value === value ? ' is-selected' : '');
        li.id = built.list.id + '-opt-' + i;
        li.setAttribute('role', 'option');
        li.setAttribute('data-value', row.value);
        li.setAttribute('aria-selected', row.value === value ? 'true' : 'false');
        if (row.disabled) {
          li.setAttribute('aria-disabled', 'true');
          li.classList.add('is-disabled');
        }
        li.textContent = row.note ? row.label + ' — ' + row.note : row.label;
        built.list.appendChild(li);
        if (row.value === value) selectedLabel = row.label;
      });

      built.labelEl.textContent = selectedLabel || placeholder || '';
      built.cd.classList.toggle('is-empty', !selectedLabel);
      built.trigger.setAttribute('aria-disabled', rows.length ? 'false' : 'true');
    }

    // The mount ticks. Bar Clamp is the "off" state, exactly as on the MTL9 PDP:
    // ticking an upgrade resolves to that upgrade's variant, unticking falls back to
    // the included Bar Clamp. Radio-like — the mounts are alternatives, not extras —
    // so ticking one unticks the other.
    function renderMountTicks(built, snap, upgrades, config) {
      const signature = JSON.stringify(upgrades.map((u) => [u.value, u.label]));
      const hasIncluded = snap.options.mounts.some((m) => m.value === INCLUDED_MOUNT);

      if (built.signature !== signature) {
        built.signature = signature;
        built.group.innerHTML = '';
        built.rows = upgrades.map((choice) => {
          const label = document.createElement('label');
          label.className = 'lr-config__checkbox';

          const input = document.createElement('input');
          input.type = 'checkbox';
          input.className = 'lr-config__checkbox-input';

          const text = document.createElement('span');
          text.className = 'lr-config__checkbox-text';
          text.textContent = choice.label;

          label.appendChild(input);
          label.appendChild(text);
          built.group.appendChild(label);

          input.addEventListener('change', () => {
            if (input.checked) { config.set('mount', choice.value); return; }
            // Unticking means "just the included Bar Clamp". With no Bar Clamp
            // variant for this bike there is nothing to fall back to, so the tick
            // stays put rather than leaving the step unanswered.
            if (!hasIncluded) { input.checked = true; return; }
            config.set('mount', INCLUDED_MOUNT);
          });

          return { key: choice.value, input: input };
        });
      }

      built.rows.forEach((row) => { row.input.checked = snap.state.mount === row.key; });
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-moto-lights-buybox]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));

  window.LRMotoLightsBuybox = { init: init };
})();
